/**
 * App Component
 * Main application with React Router and providers
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { AuthInitializer } from '@/components/layout/AuthInitializer';
import { TrackingProvider } from '@/components/tracking/TrackingProvider';
import { useAuthStore } from '@/stores/authStore';

import { lazy, Suspense } from 'react';

// Eagerly import frequently-used pages to avoid lazy-load delay on navigation
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import NewAnalysisPage from '@/pages/NewAnalysis/NewAnalysisPage';
import AnalysisResultsPage from '@/pages/AnalysisResults/AnalysisResultsPage';
import AnalysisResultsListPage from '@/pages/AnalysisResultsList/AnalysisResultsListPage';
import AnalystPage from '@/pages/Analyst/AnalystPage';
import ProfilePage from '@/pages/Profile/ProfilePage';

// Lazy load less frequently visited pages
const LoginPage = lazy(() => import('@/pages/Login/LoginPage'));
const SignUpPage = lazy(() => import('@/pages/SignUp/SignUpPage'));
const MagicLinkCallback = lazy(() => import('@/pages/Login/MagicLinkCallback'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPassword/ResetPasswordPage'));
const StyleguidePage = lazy(() => import('@/pages/Styleguide/StyleguidePage'));
const SharedAnalysisPage = lazy(() => import('@/pages/SharedAnalysis/SharedAnalysisPage'));
const ShareConfirmPage = lazy(() => import('@/pages/ShareConfirm/ShareConfirmPage'));
const ImpressumPage = lazy(() => import('@/pages/Impressum/ImpressumPage'));
const DatenschutzPage = lazy(() => import('@/pages/Datenschutz/DatenschutzPage'));
const AGBPage = lazy(() => import('@/pages/AGB/AGBPage'));
const OptInSuccessPage = lazy(() => import('@/pages/OptInSuccess/OptInSuccessPage'));
const OptInUnsubscribedPage = lazy(() => import('@/pages/OptInUnsubscribed/OptInUnsubscribedPage'));
const OptInVerifyPage = lazy(() => import('@/pages/OptInVerify/OptInVerifyPage'));
const OptInAbortPage = lazy(() => import('@/pages/OptInAbort/OptInAbortPage'));
const AccessRequestVerifiedPage = lazy(() => import('@/pages/AccessRequestVerified/AccessRequestVerifiedPage'));
const MultichannelKampagnePage = lazy(() => import('@/pages/MultichannelKampagne/MultichannelKampagnePage'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Loading fallback component
 */
function PageLoader() {
  return (
    <div className="min-h-screen bg-[var(--color-white)] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-pulse">
          <div className="gradient-text text-2xl font-semibold">Loading...</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Catch-all redirect for unknown routes (404)
 * Logged in → dashboard, not logged in → login
 */
function CatchAllRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
}

/**
 * Main application component
 */
function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthInitializer />
          <TrackingProvider />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/signup" element={<SignUpPage />} />
              <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/styleguide" element={<StyleguidePage />} />
              <Route path="/shared/:shareToken" element={<SharedAnalysisPage />} />
              <Route path="/share/confirm" element={<ShareConfirmPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />
              <Route path="/datenschutz" element={<DatenschutzPage />} />
              <Route path="/agb" element={<AGBPage />} />
              <Route path="/opt-in/success" element={<OptInSuccessPage />} />
              <Route path="/opt-in/unsubscribed" element={<OptInUnsubscribedPage />} />
              <Route path="/api/optin/verify" element={<OptInVerifyPage />} />
              <Route path="/api/optin/unsubscribe" element={<OptInAbortPage />} />
              <Route path="/access-request/verified" element={<AccessRequestVerifiedPage />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis/new"
                element={
                  <ProtectedRoute>
                    <NewAnalysisPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis/multichannel-kampagne"
                element={
                  <ProtectedRoute>
                    <MultichannelKampagnePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis/:analysisId"
                element={
                  <ProtectedRoute>
                    <AnalysisResultsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis-results"
                element={
                  <ProtectedRoute>
                    <AnalysisResultsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analyst"
                element={
                  <ProtectedRoute>
                    <AnalystPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 - redirect based on auth state */}
              <Route path="*" element={<CatchAllRedirect />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
